/**
 * Dialog for inviting a user to join the tenant.
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, Check, Mail } from 'lucide-react';
import { copyToClipboard } from '../../shared/utils/clipboard';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useToast } from '../ui/use-toast';
import { useCreateInvitationApiV1InvitationsPost } from '../../generated/api/invitations/invitations';
import { useAuth } from '../../shared/auth';
import { getErrorMessage, hasErrorCode } from '../../shared/utils/errors';
import { cn } from '../../lib/utils';
import { ModuleAccessSection } from '../../shared/components/admin/ModuleAccessSection';
import { MultiCompanyPicker } from '../../shared/components/common';
import { useMyCompanies } from '@/shared/users/hooks/useCompanies';
import { TOGGLEABLE_MODULE_IDS } from '../../shared/config/modules.config';

interface InviteUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function InviteUserDialog({ open, onOpenChange }: InviteUserDialogProps) {
  const { t } = useTranslation(['user_management', 'common']);
  const { user } = useAuth();
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [enabledModules, setEnabledModules] = useState<string[] | null>(null);
  const [companyIds, setCompanyIds] = useState<string[]>([]);
  const [companyError, setCompanyError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [invitationUrl, setInvitationUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Multi-company tenants: the invitee must be granted ≥1 company so they are
  // never left unassigned. Hidden entirely in single-company tenants.
  const { data: myCompaniesData } = useMyCompanies();
  const allCompanies = useMemo(() => myCompaniesData?.data ?? [], [myCompaniesData]);
  const isMultiCompany = allCompanies.length > 1;

  // Seed the picker with the inviter's primary company ONCE per dialog open
  // (after companies load) — not whenever the selection empties, so the admin
  // can still clear it to trigger the "at least one required" validation.
  const seededRef = useRef(false);
  useEffect(() => {
    if (!open) {
      seededRef.current = false;
      return;
    }
    if (seededRef.current || !isMultiCompany || allCompanies.length === 0) return;
    seededRef.current = true;
    const primaryId = user?.primary_company_id;
    if (primaryId && allCompanies.some((c) => c.id === primaryId)) {
      setCompanyIds([primaryId]);
    }
  }, [open, isMultiCompany, allCompanies, user?.primary_company_id]);

  // Compute available modules for the inviter
  const tenantEnabledModules = user?.tenant_enabled_modules;
  const inviterCeiling = user?.module_ceiling;
  // Admin: all tenant modules. Non-admin: intersection of their ceiling and enabled modules.
  const inviterAvailableModules = (() => {
    let modules = tenantEnabledModules ?? [...TOGGLEABLE_MODULE_IDS];
    if (!user?.is_admin) {
      // Non-admin: limit to their own ceiling
      if (inviterCeiling) {
        const ceilingSet = new Set<string>(inviterCeiling);
        modules = modules.filter(m => ceilingSet.has(m));
      }
      // Further limit to their own enabled modules
      if (user?.enabled_modules) {
        const enabledSet = new Set<string>(user.enabled_modules);
        modules = modules.filter(m => enabledSet.has(m));
      }
    }
    return modules;
  })();

  const createInvitation = useCreateInvitationApiV1InvitationsPost();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailError(null);
    setCompanyError(null);
    if (!email.trim()) return;

    // Multi-company tenants require at least one company so the invitee is
    // never left without access.
    if (isMultiCompany && companyIds.length === 0) {
      setCompanyError(t('user_management:inviteUser.companyRequired'));
      return;
    }

    try {
      const result = await createInvitation.mutateAsync({
        data: {
          email: email.trim(),
          is_admin: isAdmin,
          enabled_modules: enabledModules,
          company_ids: isMultiCompany ? companyIds : null,
        },
      });

      setInvitationUrl(result.invitation_url);
      toast({
        title: t('user_management:inviteUser.success'),
        description: t('user_management:inviteUser.emailSent', { email: result.email }),
      });
    } catch (error) {
      // Show validation-type errors inline on the email field
      if (hasErrorCode(error, 'USER_ALREADY_EXISTS') || hasErrorCode(error, 'INVALID_EMAIL_FORMAT')) {
        setEmailError(getErrorMessage(error));
      } else {
        // Show other errors as toast
        toast({
          title: t('common:app.error'),
          description: getErrorMessage(error),
          variant: 'destructive',
        });
      }
    }
  };

  const handleCopyLink = async () => {
    if (!invitationUrl) return;
    try {
      await copyToClipboard(invitationUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast({
        title: t('common:app.copied'),
      });
    } catch {
      toast({
        title: t('common:app.copyFailed'),
        variant: 'destructive',
      });
    }
  };

  const handleClose = () => {
    setEmail('');
    setIsAdmin(false);
    setEnabledModules(null);
    setCompanyIds([]);
    setCompanyError(null);
    setEmailError(null);
    setInvitationUrl(null);
    setCopied(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px] max-h-[90vh] flex flex-col">
        <DialogHeader className="shrink-0">
          <DialogTitle>{t('user_management:inviteUser.title')}</DialogTitle>
          <DialogDescription>
            {t('user_management:inviteUser.description')}
          </DialogDescription>
        </DialogHeader>

        {!invitationUrl ? (
          <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
            <div className="-mx-1 grid min-h-0 flex-1 gap-3 overflow-y-auto px-1 py-2">
              <div className="grid gap-2">
                <Label htmlFor="email">{t('common:app.email')}</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder={t('user_management:inviteUser.emailPlaceholder')}
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (emailError) setEmailError(null);
                  }}
                  className={cn(emailError && 'border-destructive')}
                  required
                  autoFocus
                />
                {emailError && (
                  <p className="text-sm text-destructive">{emailError}</p>
                )}
              </div>
              {user?.is_admin && (
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="is_admin"
                    checked={isAdmin}
                    onCheckedChange={(checked) => setIsAdmin(checked === true)}
                  />
                  <Label htmlFor="is_admin" className="cursor-pointer">
                    {t('user_management:inviteUser.inviteAsAdmin')}
                  </Label>
                </div>
              )}
              {isMultiCompany && (
                <div className="space-y-1.5">
                  <MultiCompanyPicker
                    label={t('user_management:inviteUser.companies')}
                    value={companyIds}
                    onChange={(ids) => {
                      setCompanyIds(ids);
                      if (companyError) setCompanyError(null);
                    }}
                    helperText={t('user_management:inviteUser.companiesHelp')}
                  />
                  {companyError && (
                    <p className="text-sm text-destructive">{companyError}</p>
                  )}
                </div>
              )}
              <div className="space-y-2">
                <Label>{t('user_management:inviteUser.moduleAccess')}</Label>
                <p className="text-sm text-muted-foreground">
                  {t('user_management:inviteUser.moduleAccessDescription')}
                </p>
                <ModuleAccessSection
                  enabledModules={enabledModules}
                  onChange={setEnabledModules}
                  availableModules={inviterAvailableModules}
                  showAllToggle={true}
                  showAllModules={false}
                />
              </div>
            </div>
            <DialogFooter className="mt-4 shrink-0">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
              >
                {t('common:app.cancel')}
              </Button>
              <Button
                type="submit"
                disabled={!email.trim() || createInvitation.isPending}
              >
                <Mail className="me-2 h-4 w-4" />
                {createInvitation.isPending
                  ? t('common:app.sending')
                  : t('user_management:inviteUser.sendInvitation')}
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col">
            <div className="-mx-1 min-h-0 flex-1 overflow-y-auto px-1 py-2">
              <div className="rounded-lg bg-muted p-4">
                <p className="mb-2 text-sm text-muted-foreground">
                  {t('user_management:inviteUser.linkReady')}
                </p>
                <div className="flex items-center gap-2">
                  <Input
                    readOnly
                    value={invitationUrl}
                    className="text-sm"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={handleCopyLink}
                  >
                    {copied ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
            </div>
            <DialogFooter className="mt-4 shrink-0">
              <Button onClick={handleClose}>
                {t('common:app.done')}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
